import { hasYesOrNo } from 'models/validate';
import { educationRequiresReference, educationRequiresPhysicalLocationAndReference } from 'models/education';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DEFAULT_LATEST, TODAY } from 'constants/dateLimits';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import name from 'forms/shared/validators/models/name/name';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import {
  setToStartOfMonth, getSafeDateTime, getApplicantRequiredDuration, getThresholdYears, isSafeDateLater
} from 'helpers/date';
import { SF852013 } from 'constants/formTypes';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { educationAddressNameFields } from 'constants/enums/people';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';

import { educationVerifierTypes } from 'constants/enums/educationOptions';
import { REQUIRED } from 'constants/errors';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import {
  HistoryEducationModel,
  HistoryEducationDiplomaModel,
  HistoryEducationItemModel,
} from './types';

const historyEducationDiplomaValidator: ValidateJSModel<HistoryEducationDiplomaModel> = {
  Diploma: { presence: true, hasValue: true },

  DiplomaOther: (value, attributes) => {
    if (attributes?.Diploma?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  Date: (value, attributes, attributeName, options) => {
    // School enrollment date
    const earliestDate = options?.Dates?.from;

    return {
      presence: true,
      date: {
        requireDay: false,
        requireYear: false,
        earliest: setToStartOfMonth(earliestDate),
        tooEarly: 'error.education.diplomas.date.tooEarly',
        latest: DEFAULT_LATEST,
      },
    };
  },

  Has: {},
};

export const historyEducationItemValidator: ValidateJSModel<HistoryEducationItemModel> = {
  Dates: (value, attributes, attributeName, options) => {
    const {
      applicantBirthdate, HasAttended, HasDegree10, formTypeConfig, formType
    } = options;
    const { Diplomas } = attributes;

    /*
    * When adding validation for PVQ, please make sure that there's no app breaking issues when a user
    * tries to Review the history section with no from or to dates in this date range.
    */
    if (isPVQForm(formType)) {
      return {
        presence: true,
        daterange: {
          earliest: setToStartOfMonth(applicantBirthdate),
          latest: DEFAULT_LATEST,
        }
      };
    }

    // shouldn't display validation until diploma branch question is filled
    const diplomaBranchFilled = Array.isArray(Diplomas?.items)
      && Diplomas?.items.some((obj) => obj?.Item?.Has?.value === 'No');

    const thresholdYears = getThresholdYears(options?.formType);
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 })));

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: formTypeConfig?.HISTORY_EDUCATION_YEARS ? { years: formTypeConfig?.HISTORY_EDUCATION_YEARS } : undefined,
      applicantBirthdate,
      thresholdYears,
      type: 'education'
    });

    const toDate = getSafeDateTime(value?.to);
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus(coverage)));
    let earliestDate;

    if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
    } else if (requiredDate?.valid) {
      earliestDate = requiredDate;
    }

    const beyondRequired = (toDate?.valid && earliestDate?.valid)
      ? isSafeDateLater(earliestDate, toDate) : false;

    if (HasAttended?.value === 'Yes' && diplomaBranchFilled) {
      const hasDiploma = options?.formType !== SF852013
        && Array.isArray(Diplomas?.items)
        && Diplomas?.items.some((obj) => obj?.Item?.Has?.value === 'Yes');
      if (hasDiploma) {
        return {
          presence: true,
          daterange: {
            earliest: setToStartOfMonth(applicantBirthdate),
            latest: DEFAULT_LATEST,
          },
        };
      }

      return {
        presence: true,
        daterange: {
          requireYear: false,
          earliest: beyondRequired ? requiredDate.datetime : setToStartOfMonth(applicantBirthdate),
          latest: DEFAULT_LATEST,
          tooEarly: beyondRequired ? 'error.beforeDuration' : 'error.applicantMin',
        },
      };
    }
    if (HasDegree10?.value === 'Yes') {
      const requiredEndDate = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: formTypeConfig?.HISTORY_EDUCATION_YEARS })));
      const afterRequired = (toDate.valid && requiredEndDate.valid) ? (toDate?.datetime >= requiredEndDate?.datetime) : false;
      const latest = afterRequired ? { latest: requiredEndDate.datetime, tooLate: 'history.education.error.afterDuration' } : { latest: DEFAULT_LATEST };
      return {
        presence: true,
        daterange: {
          earliest: setToStartOfMonth(applicantBirthdate),
          ...latest
        },
      };
    }
    return {};
  },

  Address: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: { validator: pvqLocationModelValidator },
      };
    }
    return {
      presence: true,
      location: { validator: locationAddressModelValidator },
    };
  },

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return {
      presence: true,
      hasValue: true,
    };
  },

  Type: {
    presence: true,
    hasValue: true,
  },

  ReferenceName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { Dates, ReferenceNameNotApplicable } = attributes;
    const isPVQ = isPVQForm(options?.formType);
    if (educationRequiresReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && ReferenceNameNotApplicable?.applicable !== false) { // undefined and true both trigger validation
      return {
        presence: true,
        model: {
          validator: name,
          hideMiddleName: !isPVQ,
        },
      };
    }
    return {};
  },

  ReferenceType: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (educationRequiresReference(Dates)
        && options?.requireHistoryEducationReferenceFields) {
        return {
          presence: true,
          array: {
            validator: {
              presence: true,
              inclusion: educationVerifierTypes,
            },
            length: { minimum: 1, message: REQUIRED },
          },
        };
      }
    }
    return {};
  },

  ReferenceTypeOtherExplanation: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (educationRequiresReference(Dates)
        && options?.requireHistoryEducationReferenceFields
        && attributes?.ReferenceType?.values?.includes('Other')) {
        return {
          presence: true,
          hasValue: true,
        };
      }
    }
    return {};
  },

  ReferenceNameNotApplicable: {},

  ReferencePhone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { Dates, ReferencePhoneNotApplicable } = attributes;
    if (educationRequiresReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && ReferencePhoneNotApplicable?.applicable !== false) { // undefined and true both trigger validation
      return {
        presence: true,
        model: { validator: phoneModelValidator, requireNumber: true },
      };
    }
    return {};
  },

  ReferencePhoneNotApplicable: {},

  ReferenceEmails: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (isPVQForm(options?.formType)) {
      if (educationRequiresReference(Dates)
        && options?.requireHistoryEducationReferenceFields) {
        if (value?.items?.[0]?.Item?.Has?.value === 'No') {
          return {};
        }

        return {
          presence: true,
          branchCollection: {
            validator: {
              Email: {
                model: {
                  validator: emailModelValidator,
                  requireEmailType: false
                },
              },
            },
          },
        };
      }
    }
    return {};
  },

  ReferenceEmail: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { Dates, ReferenceEmailNotApplicable } = attributes;
    if (educationRequiresReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && ReferenceEmailNotApplicable?.applicable !== false) { // undefined and true both trigger validation
      return {
        hasValue: { validator: { email: true } },
      };
    }

    return {};
  },

  ReferenceEmailNotApplicable: {},

  ReferenceAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const { Dates, ReferenceAddressNotApplicable } = attributes;
    if (educationRequiresReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && ReferenceAddressNotApplicable?.applicable !== false) { // undefined and true both trigger validation
      return {
        presence: true,
        location: { validator: locationAddressModelValidator },
      };
    }
    return {};
  },

  ReferenceAddressNotApplicable: {},

  Diplomas: (value, attributes) => ({
    presence: true,
    branchCollection: {
      validator: historyEducationDiplomaValidator,
      Dates: attributes?.Dates
    }
  }),

};

export const pvqHistoryEducationListItemValidator: ValidateJSModel<Partial<HistoryEducationItemModel>> = {
  Name: {
    presence: true,
    hasValue: true,
  },

  Type: {
    presence: true,
    hasValue: true
  },

  TypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.Type?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  LearningExperience: {
    presence: true,
    hasValue: true
  },

  LearningExperienceOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.LearningExperience?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  Dates: (value, attributes, attributeName, options) => {
    const { applicantBirthdate } = options;

    if (!applicantBirthdate) { return {}; }
    if (!value?.to) { return { presence: true }; }
    const endDate: SafeDateTime = getSafeDateTime(value.to);
    const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));
    const isEducationTooOld: boolean = isSafeDateLater(fiveYearsAgo, endDate);

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: isEducationTooOld ? setToStartOfMonth(fiveYearsAgo)?.datetime : applicantBirthdate,
        tooEarly: isEducationTooOld ? 'error.beforeDuration' : 'error.applicantMin',
        latest: DEFAULT_LATEST
      }
    };
  },

  Address: {
    presence: true,
    location: { validator: pvqLocationModelValidator },
  },

  Diplomas: (value, attributes) => ({
    presence: true,
    branchCollection: {
      validator: historyEducationDiplomaValidator,
      Dates: attributes?.Dates
    }
  }),

  // --------- all below only required for recent education items

  PhysicalLocations: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (
      educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields
    ) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Location: {
              location: {
                validator: pvqLocationModelValidator,
                requireIsMilitaryDiplomaticAddress: true,
                requireMilitaryDiplomaticInstallationName: true,
              },
            },
          },
        },
      };
    }

    return {};
  },

  ReferenceName: (value, attributes, attributeName, options) => {
    const { Dates, ReferenceNameNotApplicable } = attributes;
    if (educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && ReferenceNameNotApplicable?.applicable !== false) { // undefined and true both trigger validation
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
        },
      };
    }
    return {};
  },

  ReferenceType: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields) {
      return {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: educationVerifierTypes,
          },
          length: { minimum: 1, message: REQUIRED },
        },
      };
    }
    return {};
  },

  ReferenceTypeOtherExplanation: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;

    if (educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && attributes?.ReferenceType?.values?.includes('Other')) {
      return {
        presence: true,
        hasValue: true,
      };
    }

    return {};
  },

  ReferencePhones: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields) {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: { validator: phoneModelValidator },
            },
          },
        },
      };
    }

    return {};
  },

  ReferenceEmails: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields) {
      if (value?.items?.[0]?.Item?.Has?.value === 'No') {
        return {};
      }

      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator
              },
            },
          },
        },
      };
    }
    return {};
  },

  ReferenceAddress: (value, attributes, attributeName, options) => {
    const { Dates } = attributes;
    if (
      educationRequiresPhysicalLocationAndReference(Dates)
      && options?.requireHistoryEducationReferenceFields
      && attributes.ReferenceAddressNotApplicable?.applicable !== false
    ) {
      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireIsMilitaryDiplomaticAddress: true
        },
      };
    }
    return {};
  },
};

export const pvqHistoryEducationListDegreesItemValidator: ValidateJSModel<Partial<HistoryEducationItemModel>> = {
  Name: {
    presence: true,
    hasValue: true,
  },

  Type: {
    presence: true,
    hasValue: true
  },

  TypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.Type?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  LearningExperience: {
    presence: true,
    hasValue: true
  },

  LearningExperienceOtherExplanation: (value, attributes, attributeName, options) => {
    if (attributes?.LearningExperience?.value === 'Other') {
      return {
        presence: true,
        hasValue: true,
      };
    }
    return {};
  },

  Dates: (value, attributes, attributeName, options) => {
    const { applicantBirthdate } = options;

    if (!applicantBirthdate) { return {}; }
    if (!value?.to) { return { presence: true }; }
    const endDate: SafeDateTime = getSafeDateTime(value.to);
    const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));
    const isEducationTooNew = !isSafeDateLater(fiveYearsAgo, endDate);

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: applicantBirthdate,
        tooEarly: 'error.applicantMin',
        latest: fiveYearsAgo.datetime,
        tooLate: 'error.afterDuration',
      }
    };
  },

  Address: {
    presence: true,
    location: { validator: pvqLocationModelValidator },
  },

  Diplomas: (value, attributes) => ({
    presence: true,
    branchCollection: {
      validator: historyEducationDiplomaValidator,
      Dates: attributes?.Dates
    }
  }),
};

const historyEducationValidator: ValidateJSModel<HistoryEducationModel> = {
  HasAttended: {
    hasValue: { validator: hasYesOrNo },
  },

  HasDegree10: (value, attributes) => {
    // Only required if HasAttended is "No"
    if (attributes?.HasAttended?.value === 'No') {
      return {
        hasValue: { validator: hasYesOrNo },
      };
    }

    return {};
  },

  ListDegrees: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.HasDegree10?.value === 'Yes') {
        let additionalValidation = {};
        const { errors = [] } = getDuplicateNamesAndAddresses(value?.items, options?.peopleWhoKnowYou, educationAddressNameFields);
        if (errors.length > 0) {
          additionalValidation = {
            requireTrue: { // force error message; value will never be true
              message: 'relationships.people.error.duplicateListItems',
            }
          };
        }
        return {
          ...additionalValidation,
          presence: true,
          accordion: { validator: pvqHistoryEducationListDegreesItemValidator },
        };
      }
    }
    return {};
  },

  List: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.HasAttended?.value === 'Yes') {
        let additionalValidation = {};
        const { errors = [] } = getDuplicateNamesAndAddresses(value?.items, options?.peopleWhoKnowYou, educationAddressNameFields);
        if (errors.length > 0) {
          additionalValidation = {
            requireTrue: { // force error message; value will never be true
              message: 'relationships.people.error.duplicateListItems',
            }
          };
        }
        return {
          ...additionalValidation,
          presence: true,
          accordion: { validator: pvqHistoryEducationListItemValidator },
        };
      }
      return {};
    }

    // Only required if either HasAttended or HasDegree10 is yes
    if (attributes?.HasAttended?.value === 'Yes' || attributes?.HasDegree10?.value === 'Yes') {
      let additionalValidation = {};
      const { errors = [] } = getDuplicateNamesAndAddresses(value?.items, options?.peopleWhoKnowYou, educationAddressNameFields);
      if (errors.length > 0) {
        additionalValidation = {
          requireTrue: { // force error message; value will never be true
            message: 'relationships.people.error.duplicateListItems',
          }
        };
      }

      return {
        ...additionalValidation,
        presence: true,
        accordion: { validator: historyEducationItemValidator, HasDegree10: attributes?.HasDegree10, HasAttended: attributes?.HasAttended },
      };
    }

    return {};
  },
};

export default historyEducationValidator;
